/**
 * Simple in-memory LRU cache implementation with TTL support
 */

export interface CacheOptions {
  /** Maximum number of items to store in the cache (default: 100) */
  maxSize?: number;
  /** Default TTL for cache entries in milliseconds (default: 5 minutes) */
  defaultTTL?: number;
}

interface CacheEntry<T> {
  /** The cached value */
  value: T;
  /** Timestamp when this entry expires */
  expiresAt: number;
  /** Last accessed timestamp for LRU eviction */
  lastAccessed: number;
}

export class LRUCache<T> {
  private cache: Map<string, CacheEntry<T>>;
  private maxSize: number;
  private defaultTTL: number;

  /**
   * Creates a new LRU cache with optional size and TTL configuration
   * @param options Cache configuration options
   */
  constructor(options: CacheOptions = {}) {
    this.cache = new Map();
    this.maxSize = options.maxSize ?? 100;
    this.defaultTTL = options.defaultTTL ?? 5 * 60 * 1000; // Default 5 minutes
  }

  /**
   * Set a value in the cache with an optional TTL
   * @param key Cache key
   * @param value Value to cache
   * @param ttl Optional TTL in milliseconds (overrides default)
   */
  set(key: string, value: T, ttl?: number): void {
    // Clean expired entries before adding a new one
    this.cleanExpired();

    // If cache is full, evict least recently used item
    if (this.cache.size >= this.maxSize) {
      this.evictLRU();
    }

    const expiresAt = Date.now() + (ttl ?? this.defaultTTL);
    
    this.cache.set(key, {
      value,
      expiresAt,
      lastAccessed: Date.now()
    });
  }

  /**
   * Get a value from the cache if it exists and hasn't expired
   * @param key Cache key to retrieve
   * @returns The cached value or undefined if not found or expired
   */
  get(key: string): T | undefined {
    const entry = this.cache.get(key);
    
    // If entry doesn't exist or has expired, return undefined
    if (!entry || entry.expiresAt <= Date.now()) {
      if (entry) this.cache.delete(key); // Clean up expired entry
      return undefined;
    }
    
    // Update last accessed time for LRU tracking
    entry.lastAccessed = Date.now();
    return entry.value;
  }

  /**
   * Check if a key exists in the cache and hasn't expired
   * @param key Cache key to check
   * @returns True if the key exists and hasn't expired
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    return !!entry && entry.expiresAt > Date.now();
  }

  /**
   * Delete a key from the cache
   * @param key Cache key to delete
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /** Clear all items from the cache */
  clear(): void {
    this.cache.clear();
  }

  /** Get the current number of items in the cache */
  get size(): number {
    this.cleanExpired(); // Count only non-expired items
    return this.cache.size;
  }

  /**
   * Remove all expired entries from the cache
   * @private
   */
  private cleanExpired(): void {
    const now = Date.now();
    Array.from(this.cache.entries()).forEach(([key, entry]) => {
      if (entry.expiresAt <= now) {
        this.cache.delete(key);
      }
    });
  }

  /**
   * Evict the least recently used item from the cache
   * @private
   */
  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestAccess = Date.now();

    Array.from(this.cache.entries()).forEach(([key, entry]) => {
      if (entry.lastAccessed < oldestAccess) {
        oldestAccess = entry.lastAccessed;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }
} 